import { useState, useCallback, useRef, useEffect } from 'react';

export const DRIVE_PREFIX = 'drive:';
export const FOLDER_PREFIX = 'folder:';

const getFileType = (fileName) => {
  if (!fileName) return 'default';
  const extension = fileName.split('.').pop()?.toLowerCase();
  return extension || 'default';
};

const getFolderId = (folder) =>
  folder.folder_uuid || folder.folderUuid || folder.id || folder.uuid;

const getParentFolderId = (folder) =>
  folder.parent_folder_uuid || folder.parentFolderUuid;

const toFolderTreeNode = (folder, vaultUuid) => {
  const folderId = getFolderId(folder);
  return {
    id: folderId,
    name: folder.name,
    type: 'folder',
    folder_uuid: folderId,
    vault_uuid: vaultUuid,
  };
};

const normalizeRootFolders = (folders, vaultUuid) =>
  folders
    .filter((folder) => {
      const folderId = getFolderId(folder);
      const parentId = getParentFolderId(folder);
      return parentId !== folderId;
    })
    .map((folder) => toFolderTreeNode(folder, vaultUuid));

const normalizeDocuments = (documents) =>
  documents.map((doc) => {
    const docId =
      doc.document_uuid || doc.documentUuid || doc.id || doc.uuid;
    const file_uuid =
      doc.file_uuid ?? doc.fileUuid ?? undefined;
    const file_id = doc.file_id ?? doc.fileId ?? undefined;
    return {
      id: docId,
      name: doc.name,
      type: getFileType(doc.name),
      document_uuid: docId,
      ...(file_uuid != null && { file_uuid }),
      ...(file_id != null && { file_id }),
    };
  });

const normalizeChildFolders = (folders, folderUuid, vaultUuid) =>
  folders
    .filter((folder) => {
      const folderId = getFolderId(folder);
      const parentId = getParentFolderId(folder);
      if (folderId === folderUuid) return false;
      if (parentId === folderId) return false;
      return true;
    })
    .map((folder) => toFolderTreeNode(folder, vaultUuid));

/**
 * Árbol del selector de drive: carga raíz por vault y hijos por carpeta.
 *
 * ### Formato de `folders` y `documents` (filas crudas)
 * Igual que listados por tenant típicos: arrays de objetos con **cualquiera** de estos alias por fila.
 *
 * **Carpeta:** `folder_uuid` o `folderUuid` o `id` o `uuid`; `name`; `parent_folder_uuid` / `parentFolderUuid` (opcional).
 *
 * **Documento:** `document_uuid` o `documentUuid` o `id` o `uuid`; `name`; opcional `file_uuid` / `fileUuid`, `file_id` / `fileId`.
 *
 * El hook filtra carpetas inválidas (parent = self, etc.) y deduce el tipo de archivo por extensión del `name`.
 *
 * @param {object} params
 * @param {Array<{ id: string, name: string }>} params.drives — `id` = vault UUID (reserved for callers / future cache keys; tree resets on close via `isOpen`).
 * @param {boolean} params.isOpen — When `false`, clears in-memory tree, loading flags, and fetch dedupe refs (fresh load next open).
 * @param {(vaultUuid: string) => Promise<{ folders?: object[], documents?: object[] }>} params.fetchDriveRoot
 * @param {(args: { folderUuid: string, vaultUuid: string }) => Promise<{ folders?: object[], documents?: object[] }>} params.fetchFolderChildren
 */
export function useDrivePickerTree({
  drives,
  isOpen,
  fetchDriveRoot,
  fetchFolderChildren,
}) {
  const [treeData, setTreeData] = useState({});
  const [loadingFolders, setLoadingFolders] = useState({});
  const [loadingDrives, setLoadingDrives] = useState({});

  const loadingRef = useRef({});
  const loadedRef = useRef({});

  useEffect(() => {
    if (isOpen) return;
    setTreeData({});
    setLoadingFolders({});
    setLoadingDrives({});
    loadedRef.current = {};
    loadingRef.current = {};
  }, [isOpen]);

  const loadDriveContent = useCallback(
    async (vaultUuid) => {
      if (!vaultUuid || typeof fetchDriveRoot !== 'function') return;
      const key = `${DRIVE_PREFIX}${vaultUuid}`;
      if (loadedRef.current[key]) return;
      if (loadingRef.current[key]) return;

      loadingRef.current[key] = true;
      setLoadingDrives((prev) => ({ ...prev, [vaultUuid]: true }));

      try {
        const { folders: rawFolders = [], documents: rawDocuments = [] } =
          await fetchDriveRoot(vaultUuid);

        const rootFolders = normalizeRootFolders(rawFolders, vaultUuid);
        const rootDocuments = normalizeDocuments(rawDocuments);

        setTreeData((prev) => ({
          ...prev,
          [key]: { folders: rootFolders, documents: rootDocuments },
        }));

        loadedRef.current[key] = true;
      } catch {
        /* Host may toast / log failed fetch */
      } finally {
        delete loadingRef.current[key];
        setLoadingDrives((prev) => {
          const { [vaultUuid]: removed, ...rest } = prev;
          return rest;
        });
      }
    },
    [fetchDriveRoot]
  );

  const loadFolderChildren = useCallback(
    async (folderUuid, vaultUuid) => {
      if (
        !vaultUuid ||
        !folderUuid ||
        typeof fetchFolderChildren !== 'function'
      )
        return;
      const key = `${FOLDER_PREFIX}${folderUuid}`;
      if (loadedRef.current[key]) return;
      if (loadingRef.current[key]) return;

      loadingRef.current[key] = true;
      setLoadingFolders((prev) => ({ ...prev, [folderUuid]: true }));

      try {
        const { folders: rawFolders = [], documents: rawDocuments = [] } =
          await fetchFolderChildren({ folderUuid, vaultUuid });

        const childFolders = normalizeChildFolders(
          rawFolders,
          folderUuid,
          vaultUuid
        );
        const childDocuments = normalizeDocuments(rawDocuments);

        setTreeData((prev) => ({
          ...prev,
          [key]: { folders: childFolders, documents: childDocuments },
        }));

        loadedRef.current[key] = true;
      } catch {
        /* Host may toast / log failed fetch */
      } finally {
        delete loadingRef.current[key];
        setLoadingFolders((prev) => {
          const { [folderUuid]: removed, ...rest } = prev;
          return rest;
        });
      }
    },
    [fetchFolderChildren]
  );

  return {
    treeData,
    loadingFolders,
    loadingDrives,
    loadDriveContent,
    loadFolderChildren,
    DRIVE_PREFIX,
    FOLDER_PREFIX,
  };
}
